import React, { useEffect, useState } from 'react'
import kebabCase from 'lodash.kebabcase'
import clsx from 'clsx'
import { StoryblokRichtext } from 'storyblok-rich-text-react-renderer'
import * as style from '@/components/organisms/MarkdownContent/MarkdownContent.module.css'
import { MarkdownBody } from '@/components/atoms/MarkdownBody/MarkdownBody'
import { RichTextField } from '@/components/organisms/RichTextField/RichTextField'

// Helper function: Parse all URL query parameters into an object
const getQueryParams = (): Record<string, string> => {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const queryParams: Record<string, string> = {}

  params.forEach((value, key) => {
    queryParams[key] = value
  })

  return queryParams
}

// Helper function: Format date params like event_start_time and event_end_time
const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return !Number.isNaN(date.getTime()) ? date.toLocaleString() : dateString
}

// Helper function: Render a JSX element with styled placeholders
const renderWithStyledPlaceholders = (template: string, params: Record<string, string>): React.ReactNode => {
  const parts = template.split(/({.*?})/g) // Split the template into text and placeholders

  return parts.map((part) => {
    const match = part.match(/{(.*?)}/) // Check if the part is a placeholder
    if (match) {
      const key = match[1] // Extract parameter name from {key}
      let value = params[key] || part // Default to the original placeholder if key doesn't exist
      if (key === 'event_start_time' || key === 'event_end_time') {
        value = params[key] ? formatDate(params[key]) : part
      }
      return (
        <span key={value} className="font-bold">
          {value}
        </span>
      )
    }
    return <span key={part}>{part}</span> // Regular text part of the template
  })
}

export function SectionConfirmation({
  heading,
  custom_answer,
  default_answer
}: {
  heading: string
  custom_answer: string
  default_answer: StoryblokRichtext
}) {
  const [queryParams, setQueryParams] = useState<Record<string, string>>({})

  useEffect(() => {
    setQueryParams(getQueryParams())
  }, [])

  return (
    <div className="max-w-screen-desktop mx-auto px-32 py-48 font-poppins" id={kebabCase(heading)}>
      {heading && (
        <h1 className="text-headingL-mobile font-bold text-text-dark-900 text-center mb-40 laptop:text-headingL laptop:mb-64">
          {heading}
        </h1>
      )}
      <div
        className={clsx(
          'text-accent-navy-blue text-bodyL laptop:max-w-4xl laptop:mx-auto text-center [&>p_span]:break-words',
          style.content
        )}
      >
        {Object.keys(queryParams).length > 0 ? (
          <p>{renderWithStyledPlaceholders(custom_answer, queryParams)}</p>
        ) : (
          <div className={clsx('text-accent-navy-blue text-bodyL laptop:max-w-4xl laptop:mx-auto ', style.content)}>
            {typeof default_answer === 'string' ? (
              <MarkdownBody>{default_answer}</MarkdownBody>
            ) : (
              <RichTextField data={default_answer} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
